/**
 * ProShip Design System — Colors (Foundation Layer)
 *
 * Single source of truth for every color used in ProShip Merchant Panel.
 * All values derive from src/app/theme/palette.js.
 *
 * Usage:
 *   import {PRIMARY, NAVY, BORDER, SUBTLE} from '../styles/foundation'
 */
import palette from '../../theme/palette'
import {alpha} from '@mui/material/styles'

// ── Opacity helper ──────────────────────────────────────────────
export const withOpacity = (color, opacity) => alpha(color, opacity)

// ── ProShip brand ───────────────────────────────────────────────
export const PRIMARY_BLUE = palette.primary.main || '#3783e7'
export const SECONDARY_BLUE = palette.secondary.main || '#3366FF'

// ── Structural / neutral ────────────────────────────────────────
export const NAVY = palette.text.primary || '#212B36'
export const BORDER = palette.grey[300] || '#DFE3E8'
export const LIGHT_BG = palette.background.default || '#F9FAFB'
export const SUBTLE = palette.text.secondary || '#637381'
export const WHITE = palette.common.white || '#fff'
export const DISABLED = palette.grey[400] || '#C4CDD5'
export const MUTED = palette.grey[500] || '#919EAB'

// ── Severity / status ───────────────────────────────────────────
export const SUCCESS = palette.success.main || '#0abb87'
export const ERROR = palette.error.main || '#db5f5b'
export const WARNING = palette.warning.main || '#FFC107'
export const INFO = palette.info.main || '#1890FF'

// ── Extended severity ───────────────────────────────────────────
export const RED = '#e53935'
export const GREEN = '#43a047'
export const ORANGE = '#ff9800'
export const AMBER = '#ff9800'
export const TEAL = '#2ec4b6'
export const PURPLE = '#7c4dff'

// ── Backgrounds (severity) ──────────────────────────────────────
export const SUCCESS_BG = '#e8f5e9'
export const ERROR_BG = '#ffebee'
export const WARNING_BG = '#fff3e0'
export const INFO_BG = '#e3f2fd'

// ── Chart palette ───────────────────────────────────────────────
export const CHART_PALETTE = [
  PRIMARY_BLUE, SUCCESS, ORANGE, PURPLE, RED,
  '#26a69a', '#5c6bc0', '#ec407a', '#66bb6a', '#ffa726',
]

// ── Semantic status palette ─────────────────────────────────────
// One fixed colour per shipment-status bucket (keys match getOrderStatus()).
// Use this EVERYWHERE a status is coloured — charts, chips, legends — so a colour
// means exactly one thing across the whole dashboard. See the D2C viz blueprint.
export const LOST_RED = '#9c3330'
export const STATUS_COLORS = {
  ORDER_PLACED: PURPLE, // pending pickup — distinct from brand-blue (which denotes volume)
  INTRANSIT: ORANGE,
  DELIVERED: SUCCESS,
  DELIVERY_FAILED: WARNING, // NDR / delivery failed (amber)
  RTO: ERROR,
  LOST: LOST_RED,
  CANCELLED: MUTED,
  UNKNOWN: DISABLED,
}
// Human labels + canonical display order for status legends/stage bars.
export const STATUS_META = [
  {key: 'DELIVERED', label: 'Delivered'},
  {key: 'INTRANSIT', label: 'In-Transit'},
  {key: 'DELIVERY_FAILED', label: 'NDR / Failed'},
  {key: 'ORDER_PLACED', label: 'Pending Pickup'},
  {key: 'RTO', label: 'RTO'},
  {key: 'LOST', label: 'Lost'},
  {key: 'CANCELLED', label: 'Cancelled'},
]

// ── Vertical accents (per TRD — D2C / PTL / Common) ────────────
export const VERTICAL_COLORS = {
  D2C: {bg: '#FFF3E0', text: '#E65100', label: 'D2C Freight'},
  PTL: {bg: '#E8EAF6', text: '#283593', label: 'PTL Freight'},
  BOTH: {bg: '#ECEFF1', text: '#455A64', label: 'Common'},
}

// ── Font ────────────────────────────────────────────────────────
export const FONT = 'Poppins, sans-serif'

// ── Prozo Direct: operating-mode tokens ─────────────────────────
// One colour per commercial model, used by every mode chip, row tint, card
// border and chart series in the Prozo Direct module. A merchant must never
// have to ask which commercial model a number came from, so these three
// values are the whole vocabulary — do not introduce a fourth.
//   SAAS    = merchant-owned courier account (ownerType MERCHANT)
//   MANAGED = Prozo-owned courier account   (ownerType PROZO)
//   HYBRID  = PACE decides per order        (account/service-line level only)
export const MODE_SAAS = '#5B2D8E'
export const MODE_SAAS_BG = '#F3EDFA'
export const MODE_SAAS_BORDER = '#DDCBEF'

export const MODE_MANAGED = '#F57C00'
export const MODE_MANAGED_BG = '#FFF3E4'
export const MODE_MANAGED_BORDER = '#F8D9B0'

export const MODE_HYBRID = '#8E5A9E'
export const MODE_HYBRID_BG = `linear-gradient(100deg, ${MODE_SAAS_BG}, ${MODE_MANAGED_BG})`
export const MODE_HYBRID_BORDER = '#E3D2E8'

// Keyed lookup so components can resolve a mode without a switch statement.
// Mode vocabulary. The ONLY thing that varies between the three is whose courier partners
// the shipment runs on — the SaaS platform is Prozo's in all three cases. So the names name
// courier ownership, not the platform.
//
// Why not "ProShip Direct / ProShip / Hybrid": that names the PRODUCT, and a merchant on
// Hybrid is still using ProShip Direct. Using the product name for one of its own three
// modes invites "am I on Direct, or in Direct?" every time somebody reads a table.
//
//   SAAS    -> the client's own courier partners  + Prozo's SaaS platform
//   MANAGED -> Prozo's courier partners           + Prozo's SaaS platform
//   HYBRID  -> both, PACE deciding per order      + Prozo's SaaS platform
//
// `label` is for tabs, menus and the account-mode dropdown. `short` is for table chips,
// where a column repeating three words beside every row is noise.
export const MODE_COLORS = {
  SAAS: {main: MODE_SAAS, bg: MODE_SAAS_BG, border: MODE_SAAS_BORDER, label: 'Own Couriers', short: 'Own'},
  MANAGED: {main: MODE_MANAGED, bg: MODE_MANAGED_BG, border: MODE_MANAGED_BORDER, label: 'Prozo Couriers', short: 'Prozo'},
  HYBRID: {main: MODE_HYBRID, bg: MODE_HYBRID_BG, border: MODE_HYBRID_BORDER, label: 'Hybrid', short: 'Hybrid'},
}

// Credential-health badge palette (TRD 4.5). Distinct from STATUS_COLORS,
// which is shipment lifecycle — a credential is not a shipment.
export const CRED_HEALTH_COLORS = {
  HEALTHY: {text: '#157F5A', bg: '#E6F5EE'},
  AGEING: {text: '#B45309', bg: '#FDF3E3'},
  ERRORING: {text: '#C0392F', bg: '#FDECEB'},
  MANAGED: {text: '#5B6478', bg: '#FAFBFD'},
  UNTESTED: {text: '#637381', bg: '#F4F6FA'},
}

// ── Tokens added for the Prozo Direct module ────────────────────
// Every one of these replaced a raw literal that had been written inline. Rule: a colour
// used more than once, or used at all in a component, comes from here.
export const AMBER_TEXT = '#B45309' // readable amber on a pale amber fill; WARNING is too light for text
export const WHATSAPP_BUBBLE = '#E7FFDB' // the buyer-side message bubble in a WhatsApp preview
export const WHATSAPP_LINK = '#1F6FE5' // WhatsApp's own reply-button blue, not our PRIMARY_BLUE
export const ON_DARK = '#F4F6FA' // primary text on the near-black sidebar / dark cards
export const ON_DARK_MUTED = '#8A94A6' // secondary text on the same dark surfaces

// Overlay alphas for text and fills on dark surfaces. Named so a component never has to
// invent an rgba() and drift from its neighbours by a few percent.
export const ON_DARK_ALPHA = {
  strong: 'rgba(255,255,255,0.82)',
  medium: 'rgba(255,255,255,0.72)',
  soft: 'rgba(255,255,255,0.55)',
  fill: 'rgba(255,255,255,0.07)',
  fillHover: 'rgba(255,255,255,0.12)',
  border: 'rgba(255,255,255,0.14)',
  faint: 'rgba(255,255,255,0.12)',
}
